import type { ComponentDocs } from '../component-docs.js';
import { SIZE_STEPS } from '../sizes.js';

/**
 * What `<caos-page-pending>` says about itself.
 *
 * ── IT IS NOT A SPINNER YOU CAN PLACE, AND `placements: []` IS HOW THAT IS ENFORCED ──────────
 * The library's position on free-floating spinners has not moved: busy is a state OF something,
 * either of the control doing the work or of the region that is filling, and a mark drawn over a
 * layout it is hiding is worse than both. `SkeletonBlock`'s documentation says so at length and is
 * still right. What it was short of is a third case, not a change of mind.
 *
 * The third case is a route landed on from a link — an email verification, a magic-link sign-in, an
 * invitation accept. There is no button, because nobody clicked one; the person clicked a link in
 * their mail. There is no layout whose shape is worth preserving, because the page has not decided
 * what it is yet. The thing that is busy is the page, and neither of the first two answers can say
 * so.
 *
 * So this piece exists, and it declares NOWHERE. It is in the reference, because somebody has to be
 * able to find out it is there; it is not in the palette, because an author dropping a turning ring
 * onto a record page is the thing the original call was right about. The page or route that owns the
 * wait sets `pending` on it in code.
 *
 * ── WHY THE SIZE SCALE AND NOT FIVE NAMES OF ITS OWN ─────────────────────────────────────────
 * The incumbent's spinner carries five size words that mean something only to it, so a page placing
 * one beside an icon and an avatar has three vocabularies open at once. The mark here is drawn in an
 * icon's box, and an icon's box is one of the three things the platform scale (CAOS-586/594) exists
 * for, so it takes `xs`…`xl` from `sizes.ts` like the icon and the avatar do.
 *
 * ── WHY THERE IS NO `inverse` ────────────────────────────────────────────────────────────────
 * Theirs has one, and it is an attribute an author has to remember to set on exactly the dark
 * surfaces and not the light ones — which means it is wrong wherever somebody forgot, and wrong
 * everywhere the day a surface changes colour. The mark and its line are `currentColor`: they take
 * the colour of the layer they sit on. A branded hero, a white card and the dark shell all come out
 * right with nothing declared, and a brand that repaints a layer repaints this with it.
 *
 * ── THE REDUCED-MOTION TREATMENT IS THE SHAPE, NOT THE MOVEMENT ──────────────────────────────
 * Under `prefers-reduced-motion: reduce` the ring stops and is not replaced with anything, which
 * sounds like the mistake the progress bar's documentation warns about. It is not the same case: the
 * bar's moving part is the only thing distinguishing "running" from "empty track", so stopping it
 * leaves a shape that reads as stalled. This ring is three quarters of a circle at rest — a shape
 * that reads as unfinished whether or not it is turning — and the line of copy under it says the
 * same thing in words. Both are present in both treatments; only the turning goes away.
 */
const docs: ComponentDocs = {
  aliases: ['spinner', 'loader', 'page loading', 'route loading', 'busy page', 'loading screen', 'waiting'],

  answers: [
    'show that a page landed from a link is still working',
    'show a verification or a magic link being checked',
    'say the page is busy when there is no button and no layout yet',
    'find out why there is no spinner to drop onto a page',
  ],

  // NOWHERE, and deliberately. See the header: this is what keeps a free-floating spinner out of
  // the palette while still letting a route say it is waiting.
  placements: [],

  inputs: [
    {
      name: 'pending',
      attribute: 'pending',
      type: 'boolean',
      description:
        'The whole declaration: this page or route is waiting. Set, the mark and its line are drawn ' +
        'and the element reports itself busy. Unset, it draws nothing, announces nothing and takes ' +
        'up no space, so a surface can mount it once and toggle it rather than build and discard it.',
      default: false,
    },
    {
      name: 'size',
      attribute: 'size',
      type: 'text',
      options: [...SIZE_STEPS],
      description:
        'How big the mark is drawn, on the platform size scale — the same five steps the icon and ' +
        'the avatar take. A value that names no step falls back to the default rather than being ' +
        'refused.',
      default: 'lg',
    },
    {
      name: 'label',
      attribute: 'label',
      type: 'text',
      description:
        'The line under the mark, and the thing that is actually announced — say what is being ' +
        'waited on ("Confirming your email…"), not that something is happening. An empty value is ' +
        'read as asking for the default rather than for silence.',
      default: 'Loading…',
    },
  ],

  // Read from the source: it raises nothing. Whatever is doing the waiting already knows when it
  // finished — that is the moment it clears `pending` — so an event here would tell the caller a
  // thing the caller just decided.
  events: [],

  state: [],

  // Nothing is slotted. The line of copy comes in through `label` precisely so that it is the text
  // of the live region; content arriving through a slot would be outside it and never announced.
  slots: [],

  tokens: [
    { token: '--caos-font', controls: 'The typeface of the line under the mark.' },
    { token: '--caos-space-3', controls: 'The gap between the mark and its line.' },
    { token: '--caos-space-6', controls: 'The space above and below the pair.' },
    { token: '--caos-space-5', controls: 'The space to either side of them.' },
    { token: '--caos-size-xs-height', controls: 'The mark at the xs step.' },
    { token: '--caos-size-sm-height', controls: 'The mark at the sm step.' },
    { token: '--caos-size-md-height', controls: 'The mark at the md step.' },
    { token: '--caos-size-lg-height', controls: 'The mark at the lg step, which is the default.' },
    { token: '--caos-size-xl-height', controls: 'The mark at the xl step.' },
  ],

  parts: [
    { part: 'container', is: 'the centred column holding the mark and its line' },
    { part: 'mark', is: 'the ring' },
    { part: 'label', is: 'the line under it, which is also the live region' },
  ],

  methods: [],

  placing:
    'It is not placed — it declares no placement, and the palette will not offer it. A page or route ' +
    'renders it in code, usually as the only thing in the mount while a call is in flight. It is a ' +
    'block and centres itself in the width it is given; give it the height of the region it stands ' +
    'in if you want it optically centred rather than sitting at the top.',

  whenToUse:
    'The PAGE is what is waiting: a route reached from a link, before it knows what it is going to ' +
    'be. If a control started the wait, the control says so; if a region of a known shape is ' +
    'filling, the placeholder says so. This is the case where neither exists.',

  insteadUse: [
    {
      name: 'Button',
      when:
        'Somebody clicked something and that click is what is taking time. The button\'s own ' +
        '`loading` flag draws the mark on the control that caused the wait and makes it ' +
        'non-interactive, which is the whole story for a save.',
    },
    {
      name: 'SkeletonBlock',
      when:
        'A region whose shape is already known is filling. A placeholder in the shape of the content ' +
        'that is coming keeps the layout still when the data lands; a mark drawn over the top of it ' +
        'moves everything twice.',
    },
    {
      name: 'ProgressBar',
      when:
        'There is an operation to report on, with or without a number. A bar says "a job is running ' +
        'and here is how far"; this says "the page has not resolved yet".',
    },
    {
      name: 'EmptyState',
      when:
        'The wait is over and the answer was nothing. A pending state left up forever is how a person ' +
        'ends up staring at a page that has already finished failing.',
    },
  ],

  accessibility:
    'While `pending` is set the element carries `aria-busy`, and the line of copy sits in a ' +
    '`role="status"` live region so it is announced politely rather than interrupting. Clearing ' +
    '`pending` EMPTIES that region and drops `aria-busy` — the announcement is retracted rather than ' +
    'left standing by a mark that merely stopped turning, which is the difference between a reader ' +
    'being told the page resolved and a reader being told nothing more. The ring itself is ' +
    '`aria-hidden`: it says the same thing as the line beside it, and saying it twice is worse than ' +
    'once. Left to the author: giving it a `label` that names what is being waited on, and taking it ' +
    'down when the wait ends rather than when the next paint happens to remove it.',

  examples: [
    {
      key: 'waiting',
      label: 'A route that landed from a link',
      summary:
        'The whole piece: a ring and the thing being waited on, named. This is what an email ' +
        'verification page shows between the click and the answer.',
      placement: {
        component: 'caos-page-pending',
        inputs: { pending: true, label: 'Confirming your email…' },
      },
    },
    {
      key: 'reduced-motion',
      label: 'The same page, reduced motion',
      summary:
        'The same element under `prefers-reduced-motion: reduce`. The ring stops; it does not change, ' +
        'because it was already three quarters of a circle and that is the part that reads as ' +
        'unfinished. Turn the setting on and the example above becomes this one.',
      placement: {
        component: 'caos-page-pending',
        inputs: { pending: true, label: 'Confirming your email…' },
      },
    },
    {
      key: 'sizes',
      label: 'The five steps',
      summary:
        'The platform size scale, the same five the icon and the avatar take. A small step suits a ' +
        'route that resolves inside a card; the larger ones suit a page with nothing else on it.',
      placement: SIZE_STEPS.map((size) => ({
        component: 'caos-page-pending',
        inputs: { pending: true, size, label: `Loading at ${size}…` },
      })),
    },
    {
      key: 'resolved',
      label: 'After it resolves',
      summary:
        'Without `pending` there is nothing to see and nothing to hear: no mark, no line, no busy ' +
        'state, and no space taken. This is the state a surface leaves it in rather than removing it.',
      placement: {
        component: 'caos-page-pending',
        inputs: { label: 'Confirming your email…' },
      },
    },
  ],
};

export default docs;
